/**
 * Browser-model app shell: routes ↔ URLs, tabs with per-tab history, and a split-pane layout tree.
 * Pure and immutable — every op returns a new ShellState. IDs are injected (`newId`), never generated here.
 *
 * Invariants (kept by every op):
 * - there is at least one tab and one pane; every pane shows a distinct, existing tab;
 * - `focusedPaneId` names a pane and `activeTabId` is that pane's tab;
 * - pinned tabs come first in `tabs`; the number of panes is ≤ `maxPanes`;
 * - a tab is in at most one layout (`layout` or one of the background `splits`), and pane ids are unique across them;
 *   every background split has at least 2 panes (it is fitted to `maxPanes` when it comes back on screen);
 * - tab groups hold existing unpinned tabs, each tab in at most one group, side by side in `tabs` order.
 *
 * Split groups (Arc/Chrome-style, the `tabs` feature): a split view is one entry in the tab strip. Selecting a tab
 * outside it keeps the split together in `splits` (in the background); selecting any of its tabs brings it back. The
 * ops that know about this are `selectTab`, `openTab`, `closeTabs`, `splitPane`… (see "Split groups and tab groups");
 * `activateTab`, `openUrl(…, "newTab")` and `closeTab` keep the original single-layout behaviour.
 */

import type { ContactKind, DriveView, MailListFolder, MailTabId } from "@bundle/contracts";

/** A mail tab id (MailTabId): the server defines the tabs, so any well-formed one routes. */
const MAIL_TAB_ID = /^[a-z][a-z0-9-]{0,31}$/;
const MAIL_FOLDER_IDS = ["inbox", "starred", "snoozed", "scheduled", "drafts", "sent", "archive", "trash", "spam"] as const;
/** Files views (contracts `DriveView`), in sidebar order. */
export const DRIVE_VIEWS = ["my", "shared", "recent", "starred", "trash", "conversations"] as const;

/** What the mail list shows: a folder, Gmail's views (starred, snoozed, scheduled) or my drafts. */
export type MailView = MailListFolder | "drafts";

// ---------- Routes ----------

export type Route =
  | { type: "home"; ws: string }
  /** `focus`: a message to scroll to and highlight (message links, `?m=<messageId>`). */
  | { type: "channel"; ws: string; channelId: string; focus?: string }
  | { type: "dm"; ws: string; userId: string }
  | { type: "thread"; ws: string; channelId: string; rootId: string; focus?: string }
  | { type: "page"; ws: string; pageId: string }
  | { type: "db"; ws: string; dbId: string; view?: string }
  | { type: "search"; ws: string; query: string }
  | { type: "activity"; ws: string }
  /** Later: saved messages and reminders (logic/later). */
  | { type: "later"; ws: string }
  /**
   * Mail (logic/mail): a mailbox's inbox (one of the tabs the server defines, MailTabDef) or another folder.
   * No mailbox = my first one, no tab = Mail picks (mailLandingTab), `label` = one of its labels across folders.
   * `q` = the search in the box, `filter` = the filter row's chips (logic/mailQuery `mailListFilterQuery`): both stay in
   * the URL, as Gmail's do, so a reload, Back or a pane that remounts finds them. `mailboxId` / `group` = the Mailbox
   * filter: without either, all my mailboxes. `/{ws}/mail[/{mailboxId}][?group=&tab=&folder=&label=&q=&filter=]`.
   */
  | { type: "mail"; ws: string; mailboxId?: string; group?: string; tab?: MailTabId; folder?: MailView; label?: string; q?: string; filter?: string }
  /** One mail thread (`/{ws}/mail/t/{threadId}`). */
  | { type: "mailThread"; ws: string; threadId: string }
  /** Contacts, filtered by kind (Email / Clients / Team): `/{ws}/contacts[?kind=]`. */
  /** `contact` opens that contact's detail (a people card's "Open contact"). */
  | { type: "contacts"; ws: string; kind?: ContactKind; contactId?: string }
  /** Docs: every page and database I can open, recents first (`/{ws}/docs`). */
  | { type: "docs"; ws: string }
  /**
   * Calendar (logic/calendar): `/{ws}/calendar[?view=day|week|month][&at=<ts>&event=<id>]`. `at` shows the day of that
   * instant; `event` opens that event's details (Home's Today).
   */
  | { type: "calendar"; ws: string; view?: "day" | "week" | "month"; at?: number; event?: string }
  /** All unreads: every conversation with unread messages, oldest unread first. */
  | { type: "unreads"; ws: string }
  /** My scheduled messages (logic/scheduled). */
  | { type: "scheduled"; ws: string }
  /** Drafts & sent: my unsent drafts across conversations and what I sent lately (logic/drafts). */
  | { type: "drafts"; ws: string }
  | { type: "agents"; ws: string }
  | { type: "projects"; ws: string }
  | { type: "huddle"; ws: string; huddleId: string }
  /**
   * The Huddles area (logic/huddlesArea): live, upcoming and recent huddles and my links (`/{ws}/huddles`); with
   * `huddleId`, a past huddle's page (`/{ws}/huddles/{huddleId}`).
   */
  | { type: "huddles"; ws: string; huddleId?: string }
  /**
   * Files (docs/features/files.md): a view's list (`/{ws}/files` = My files, `/{ws}/files/{view}`), a folder
   * (`/{ws}/files/folders/{folderId}`) or an item's link (`/{ws}/files/d/{itemId}`: a folder lists, a file previews
   * over the list, a page opens). `q` searches (`?q=`).
   */
  | { type: "files"; ws: string; view?: DriveView; folderId?: string; itemId?: string; q?: string }
  /** A shared file's page (preview, details, who can access it). */
  | { type: "file"; ws: string; fileId: string }
  /** The Settings tab: profile, account, workspace, members… (`/{ws}/settings` opens the profile). */
  | { type: "settings"; ws: string; section: SettingsSection };

export type RouteType = Route["type"];

/**
 * Sections of the Settings tab (`/{ws}/settings/{section}`): the redesign's pages (logic/settingsSections, in its
 * order), then the slugs from before it, kept so old links and emails still parse (`resolveSettingsSection` sends
 * them to their new home).
 */
export const SETTINGS_SECTIONS = [
  "profile",
  "account",
  "notifications",
  "appearance",
  "mail",
  "calendar",
  "sidebar",
  "privacy",
  "general",
  "people",
  "billing",
  "features",
  "domains",
  "huddles",
  "import",
  "workspace",
  "device",
  "sync",
  "help",
  // Before the redesign (2026-11): parse only.
  "preferences",
  "navigation",
  "members",
  "invitations",
  "guests",
  "usergroups",
  "integrations",
  "security",
  "data",
  "stickers",
  "reports",
] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

function settingsSection(s: string): SettingsSection | undefined {
  for (const x of SETTINGS_SECTIONS) if (x === s) return x;
  return undefined;
}

/** Decodes one percent-encoded component; `+` means space only inside query strings. */
function decode(part: string, plusIsSpace: boolean): string | undefined {
  try {
    return decodeURIComponent(plusIsSpace ? part.replace(/\+/g, " ") : part);
  } catch {
    return undefined;
  }
}

function queryParam(query: string, name: string): string | undefined {
  if (query === "") return undefined;
  for (const pair of query.split("&")) {
    const eq = pair.indexOf("=");
    const k = eq < 0 ? pair : pair.slice(0, eq);
    if (decode(k, true) === name) return decode(eq < 0 ? "" : pair.slice(eq + 1), true);
  }
  return undefined;
}

/**
 * Parses an app URL (path with optional query/hash, or an absolute http(s) URL) into a Route.
 * Returns undefined for anything that is not a known route.
 */
export function parseUrl(url: string): Route | undefined {
  let rest = url.trim();
  const scheme = rest.match(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i);
  if (scheme) rest = rest.slice(scheme[0].length);
  const hashAt = rest.indexOf("#");
  if (hashAt >= 0) rest = rest.slice(0, hashAt);
  const qAt = rest.indexOf("?");
  const query = qAt >= 0 ? rest.slice(qAt + 1) : "";
  const path = qAt >= 0 ? rest.slice(0, qAt) : rest;

  const segs: string[] = [];
  for (const raw of path.split("/")) {
    if (raw === "") continue;
    const s = decode(raw, false);
    if (s === undefined || s === "") return undefined;
    segs.push(s);
  }
  const ws = segs[0];
  if (ws === undefined) return undefined;
  const kind = segs[1];
  const a = segs[2];
  const b = segs[3];
  const n = segs.length;

  if (n === 1) return { type: "home", ws };
  const focusId = queryParam(query, "m");
  if (kind === "c" && n === 3 && a !== undefined) {
    return focusId === undefined || focusId === "" ? { type: "channel", ws, channelId: a } : { type: "channel", ws, channelId: a, focus: focusId };
  }
  if (kind === "dm" && n === 3 && a !== undefined) return { type: "dm", ws, userId: a };
  if (kind === "t" && n === 4 && a !== undefined && b !== undefined) {
    return focusId === undefined || focusId === ""
      ? { type: "thread", ws, channelId: a, rootId: b }
      : { type: "thread", ws, channelId: a, rootId: b, focus: focusId };
  }
  if (kind === "p" && n === 3 && a !== undefined) return { type: "page", ws, pageId: a };
  if (kind === "db" && n === 3 && a !== undefined) {
    const view = queryParam(query, "view");
    return view === undefined || view === "" ? { type: "db", ws, dbId: a } : { type: "db", ws, dbId: a, view };
  }
  if (kind === "search" && n === 2) return { type: "search", ws, query: queryParam(query, "q") ?? "" };
  if (kind === "activity" && n === 2) return { type: "activity", ws };
  if (kind === "later" && n === 2) return { type: "later", ws };
  if (kind === "mail" && n === 4 && a === "t" && b !== undefined) return { type: "mailThread", ws, threadId: b };
  if (kind === "mail" && (n === 2 || n === 3)) {
    const r: Route = { type: "mail", ws };
    if (a !== undefined) r.mailboxId = a;
    const group = queryParam(query, "group");
    if (group !== undefined && group !== "") r.group = group;
    const tab = queryParam(query, "tab");
    const folder = queryParam(query, "folder");
    if (tab !== undefined && MAIL_TAB_ID.test(tab)) r.tab = tab;
    if (folder !== undefined && (MAIL_FOLDER_IDS as readonly string[]).includes(folder) && folder !== "inbox") r.folder = folder as MailView;
    const label = queryParam(query, "label");
    if (label !== undefined && label !== "") r.label = label;
    const search = queryParam(query, "q");
    if (search !== undefined && search.trim() !== "") r.q = search;
    const filter = queryParam(query, "filter");
    if (filter !== undefined && filter.trim() !== "") r.filter = filter;
    return r;
  }
  if (kind === "contacts" && n === 2) {
    const k = queryParam(query, "kind");
    const r: Route & { type: "contacts" } = k === "email" || k === "client" || k === "team" ? { type: "contacts", ws, kind: k } : { type: "contacts", ws };
    const contact = queryParam(query, "contact");
    if (contact !== undefined && contact !== "") r.contactId = contact;
    return r;
  }
  if (kind === "docs" && n === 2) return { type: "docs", ws };
  if (kind === "calendar" && n === 2) {
    const v = queryParam(query, "view");
    const r: Route = v === "day" || v === "month" ? { type: "calendar", ws, view: v } : { type: "calendar", ws };
    const at = queryParam(query, "at") ?? "";
    if (/^[0-9]{1,15}$/.test(at) && Number(at) > 0) r.at = Number(at);
    const event = queryParam(query, "event");
    if (event !== undefined && event !== "") r.event = event;
    return r;
  }
  if (kind === "unreads" && n === 2) return { type: "unreads", ws };
  if (kind === "scheduled" && n === 2) return { type: "scheduled", ws };
  if (kind === "drafts" && n === 2) return { type: "drafts", ws };
  if (kind === "agents" && n === 2) return { type: "agents", ws };
  if (kind === "projects" && n === 2) return { type: "projects", ws };
  if (kind === "huddle" && n === 3 && a !== undefined) return { type: "huddle", ws, huddleId: a };
  if (kind === "huddles" && n === 2) return { type: "huddles", ws };
  if (kind === "huddles" && n === 3 && a !== undefined) return { type: "huddles", ws, huddleId: a };
  if (kind === "f" && n === 3 && a !== undefined) return { type: "file", ws, fileId: a };
  if (kind === "files") {
    let r: Route | undefined;
    if (n === 2) r = { type: "files", ws };
    else if (n === 3 && a !== undefined && (DRIVE_VIEWS as readonly string[]).includes(a) && a !== "my") r = { type: "files", ws, view: a as DriveView };
    else if (n === 4 && a === "folders" && b !== undefined) r = { type: "files", ws, folderId: b };
    else if (n === 4 && a === "d" && b !== undefined) r = { type: "files", ws, itemId: b };
    if (r === undefined) return undefined;
    const q = queryParam(query, "q");
    if (r.type === "files" && q !== undefined && q.trim() !== "" && r.itemId === undefined) r.q = q;
    return r;
  }
  if (kind === "settings" && n === 2) return { type: "settings", ws, section: "profile" };
  if (kind === "settings" && n === 3 && a !== undefined) {
    const section = settingsSection(a);
    return section === undefined ? undefined : { type: "settings", ws, section };
  }
  return undefined;
}

const enc = (s: string) => encodeURIComponent(s);

/** Canonical URL for a route (inverse of parseUrl). */
export function formatUrl(route: Route): string {
  const base = `/${enc(route.ws)}`;
  switch (route.type) {
    case "home":
      return base;
    case "channel":
      return `${base}/c/${enc(route.channelId)}${focusQuery(route.focus)}`;
    case "dm":
      return `${base}/dm/${enc(route.userId)}`;
    case "thread":
      return `${base}/t/${enc(route.channelId)}/${enc(route.rootId)}${focusQuery(route.focus)}`;
    case "page":
      return `${base}/p/${enc(route.pageId)}`;
    case "db":
      return route.view === undefined || route.view === ""
        ? `${base}/db/${enc(route.dbId)}`
        : `${base}/db/${enc(route.dbId)}?view=${enc(route.view)}`;
    case "search":
      return route.query === "" ? `${base}/search` : `${base}/search?q=${enc(route.query)}`;
    case "activity":
      return `${base}/activity`;
    case "later":
      return `${base}/later`;
    case "mail": {
      const q: string[] = [];
      if (route.group !== undefined) q.push(`group=${encodeURIComponent(route.group)}`);
      if (route.tab !== undefined) q.push(`tab=${route.tab}`);
      if (route.folder !== undefined && route.folder !== "inbox") q.push(`folder=${route.folder}`);
      if (route.label !== undefined) q.push(`label=${encodeURIComponent(route.label)}`);
      if (route.q !== undefined && route.q !== "") q.push(`q=${encodeURIComponent(route.q)}`);
      if (route.filter !== undefined && route.filter !== "") q.push(`filter=${encodeURIComponent(route.filter)}`);
      const path = route.mailboxId === undefined ? `${base}/mail` : `${base}/mail/${enc(route.mailboxId)}`;
      return q.length === 0 ? path : `${path}?${q.join("&")}`;
    }
    case "mailThread":
      return `${base}/mail/t/${enc(route.threadId)}`;
    case "contacts":
    {
      const q = [...(route.kind === undefined ? [] : [`kind=${route.kind}`]), ...(route.contactId === undefined ? [] : [`contact=${enc(route.contactId)}`])];
      return q.length === 0 ? `${base}/contacts` : `${base}/contacts?${q.join("&")}`;
    }
    case "docs":
      return `${base}/docs`;
    case "calendar":
    {
      const q: string[] = [];
      if (route.view !== undefined && route.view !== "week") q.push(`view=${route.view}`);
      if (route.at !== undefined) q.push(`at=${route.at}`);
      if (route.event !== undefined) q.push(`event=${enc(route.event)}`);
      return q.length === 0 ? `${base}/calendar` : `${base}/calendar?${q.join("&")}`;
    }
    case "unreads":
      return `${base}/unreads`;
    case "scheduled":
      return `${base}/scheduled`;
    case "drafts":
      return `${base}/drafts`;
    case "agents":
      return `${base}/agents`;
    case "projects":
      return `${base}/projects`;
    case "huddle":
      return `${base}/huddle/${enc(route.huddleId)}`;
    case "huddles":
      return route.huddleId === undefined ? `${base}/huddles` : `${base}/huddles/${enc(route.huddleId)}`;
    case "file":
      return `${base}/f/${enc(route.fileId)}`;
    case "files": {
      const path =
        route.itemId !== undefined
          ? `${base}/files/d/${enc(route.itemId)}`
          : route.folderId !== undefined
            ? `${base}/files/folders/${enc(route.folderId)}`
            : route.view !== undefined && route.view !== "my"
              ? `${base}/files/${route.view}`
              : `${base}/files`;
      return route.q === undefined || route.q.trim() === "" || route.itemId !== undefined ? path : `${path}?q=${encodeURIComponent(route.q)}`;
    }
    case "settings":
      return `${base}/settings/${route.section}`;
  }
}

function focusQuery(focus: string | undefined): string {
  return focus === undefined || focus === "" ? "" : `?m=${enc(focus)}`;
}

/**
 * A message link ("Copy link", Slack's permalink): the conversation scrolled to the message, or — for a thread reply —
 * the thread scrolled to the reply. `wsSlug` is the workspace's URL slug; prefix the app origin to share it.
 */
export function messageUrl(wsSlug: string, channelId: string, messageId: string, threadRootId?: string): string {
  return threadRootId === undefined || threadRootId === messageId
    ? formatUrl({ type: "channel", ws: wsSlug, channelId, focus: messageId })
    : formatUrl({ type: "thread", ws: wsSlug, channelId, rootId: threadRootId, focus: messageId });
}

/** Canonicalizes a URL (parse + format); unknown URLs are returned trimmed and unchanged. */
export function normalizeUrl(url: string): string {
  const r = parseUrl(url);
  return r === undefined ? url.trim() : formatUrl(r);
}

// ---------- Activity bar views ----------

/**
 * The activity bar's views (VS Code's activity bar, Slack's left rail), in display order. Each view owns a sidebar:
 * Chat (channels, DMs, sections), Email (mailboxes, tabs, folders), Calendar (Create, the mini month, my calendars,
 * booking pages), Contacts (All, Team, Clients, Email contacts), Huddles (live, upcoming, recent, my links), Docs (the page tree), Files (views, folders,
 * storage). A new view is one entry here plus its routes in `viewOfRoute`.
 */
export const SHELL_VIEWS = ["chat", "email", "calendar", "contacts", "huddles", "docs", "files"] as const;
export type ShellView = (typeof SHELL_VIEWS)[number];

/** The view that owns a route's tab, or undefined for neutral tabs (home, activity, later, settings, search…). */
export function viewOfRoute(route: Route): ShellView | undefined {
  switch (route.type) {
    case "channel":
    case "dm":
    case "thread":
      return "chat";
    // A huddle's call belongs to the Huddles area, so it works with Chat off.
    case "huddle":
    case "huddles":
      return "huddles";
    case "mail":
    case "mailThread":
      return "email";
    case "calendar":
      return "calendar";
    case "contacts":
      return "contacts";
    case "page":
    case "db":
    case "docs":
      return "docs";
    case "files":
      return "files";
    default:
      return undefined;
  }
}

/** `viewOfRoute` for a tab's URL (undefined for an unknown URL). */
export function viewOfUrl(url: string): ShellView | undefined {
  const route = parseUrl(url);
  return route === undefined ? undefined : viewOfRoute(route);
}

/** A stored view name when it is one of `available`, else the first available view (Chat when none are listed). */
export function restoreView(stored: string | undefined, available: readonly ShellView[]): ShellView {
  for (const v of available) if (v === stored) return v;
  return available[0] ?? "chat";
}

/**
 * The view to show once the active tab is `url` (VS Code: the activity bar follows the editor): the tab's owner when
 * that view is available, else `current` (neutral tabs and hidden views leave the sidebar as it is).
 */
export function followTab(current: ShellView, url: string, available: readonly ShellView[]): ShellView {
  const owner = viewOfUrl(url);
  return owner !== undefined && available.includes(owner) ? owner : current;
}

// ---------- Tabs & layout ----------

export type Tab = {
  id: string;
  url: string;
  /** Older URLs, oldest first. */
  back: string[];
  /** Newer URLs after going back, nearest first. */
  forward: string[];
  pinned: boolean;
};

export type SplitDirection = "row" | "column";

export type SplitNode =
  | { type: "pane"; id: string; tabId: string }
  | { type: "split"; split: SplitDirection; ratio: number; a: SplitNode; b: SplitNode };

export type PaneNode = { type: "pane"; id: string; tabId: string };

/** Tab-group colours: the tag palette (tokens `tag.<color>.bg` / `tag.<color>.fg`), in the order new groups take them. */
export const GROUP_COLORS = ["blue", "red", "yellow", "green", "pink", "purple", "orange", "brown", "gray"] as const;
export type GroupColor = (typeof GROUP_COLORS)[number];
/** Longest group name kept. */
export const GROUP_NAME_MAX = 50;
/** Close actions ⌘⇧T can undo. */
export const CLOSED_LIMIT = 25;

/** A Chrome-style tab group: a named, coloured run of unpinned tabs. `tabIds` are in `tabs` order. */
export type TabGroup = { id: string; name: string; color: GroupColor; collapsed: boolean; tabIds: string[] };

/** The group a closed tab was in (recreated by reopening when it has gone). */
export type ClosedGroup = { id: string; name: string; color: GroupColor };

/** One close that ⌘⇧T can undo: a run of neighbouring tabs from one group (or none). */
export type ClosedEntry = {
  tabs: Tab[];
  /** Where the run was, counted in the tabs that stayed open. */
  index: number;
  group?: ClosedGroup;
  /** Split views made only of these tabs (restored with them). */
  splits: SplitNode[];
};

export type ShellState = {
  tabs: Tab[];
  activeTabId: string;
  /** The layout on screen. */
  layout: SplitNode;
  focusedPaneId: string;
  maxPanes: number;
  /** URL opened by "new tab" and when the last tab is closed (the start page). */
  homeUrl: string;
  /** Tab groups, in strip order. */
  groups: TabGroup[];
  /** Split views that are not on screen (split groups in the background), each with at least 2 panes. */
  splits: SplitNode[];
  /** Recently closed tabs, oldest first (⌘⇧T reopens the last entry). */
  closed: ClosedEntry[];
};

/** Shell state as persisted by any version: builds before tab groups wrote no `groups`, `splits` or `closed`. */
export type StoredShellState = {
  tabs: Tab[];
  activeTabId: string;
  layout: SplitNode;
  focusedPaneId: string;
  maxPanes: number;
  homeUrl: string;
  groups?: TabGroup[];
  splits?: SplitNode[];
  closed?: ClosedEntry[];
};

export type OpenTarget = "current" | "newTab" | "split";

export const MIN_RATIO = 0.15;
export const MAX_RATIO = 0.85;
export const MAX_PANES_LIMIT = 4;
/** History entries kept per direction. */
export const HISTORY_LIMIT = 50;

function clampPanes(n: number): number {
  return Math.max(1, Math.min(MAX_PANES_LIMIT, Math.floor(n)));
}

function clampRatio(r: number): number {
  return Math.max(MIN_RATIO, Math.min(MAX_RATIO, r));
}

export function createShell(homeUrl: string, tabId: string, paneId: string, maxPanes: number): ShellState {
  return {
    tabs: [{ id: tabId, url: homeUrl, back: [], forward: [], pinned: false }],
    activeTabId: tabId,
    layout: { type: "pane", id: paneId, tabId },
    focusedPaneId: paneId,
    maxPanes: clampPanes(maxPanes),
    homeUrl,
    groups: [],
    splits: [],
    closed: [],
  };
}

/** Panes in reading order (depth-first, a before b). */
export function panes(layout: SplitNode): PaneNode[] {
  if (layout.type === "pane") return [layout];
  return [...panes(layout.a), ...panes(layout.b)];
}

export function findTab(state: ShellState, tabId: string): Tab | undefined {
  return state.tabs.find((t) => t.id === tabId);
}

export function activeTab(state: ShellState): Tab {
  const t = findTab(state, state.activeTabId);
  if (t === undefined) throw new Error("shell invariant: active tab missing");
  return t;
}

export function paneOfTab(state: ShellState, tabId: string): PaneNode | undefined {
  return panes(state.layout).find((p) => p.tabId === tabId);
}

export function canGoBack(state: ShellState, tabId: string): boolean {
  const t = findTab(state, tabId);
  return t !== undefined && t.back.length > 0;
}

export function canGoForward(state: ShellState, tabId: string): boolean {
  const t = findTab(state, tabId);
  return t !== undefined && t.forward.length > 0;
}

function mapPanes(node: SplitNode, f: (p: PaneNode) => SplitNode): SplitNode {
  if (node.type === "pane") return f(node);
  return { ...node, a: mapPanes(node.a, f), b: mapPanes(node.b, f) };
}

function setPaneTab(layout: SplitNode, paneId: string, tabId: string): SplitNode {
  return mapPanes(layout, (p) => (p.id === paneId ? { ...p, tabId } : p));
}

/** Removes a pane; its sibling takes the parent split's place. Returns undefined if it is the only pane. */
function removePane(node: SplitNode, paneId: string): SplitNode | undefined {
  if (node.type === "pane") return node.id === paneId ? undefined : node;
  const a = removePane(node.a, paneId);
  const b = removePane(node.b, paneId);
  if (a === undefined) return b;
  if (b === undefined) return a;
  return { ...node, a, b };
}

function focus(state: ShellState, paneId: string): ShellState {
  const p = panes(state.layout).find((x) => x.id === paneId);
  if (p === undefined) return state;
  return { ...state, focusedPaneId: p.id, activeTabId: p.tabId };
}

function updateTab(state: ShellState, tabId: string, f: (t: Tab) => Tab): ShellState {
  return { ...state, tabs: state.tabs.map((t) => (t.id === tabId ? f(t) : t)) };
}

function navigateTab(tab: Tab, url: string): Tab {
  if (tab.url === url) return tab;
  const back = [...tab.back, tab.url];
  return { ...tab, url, back: back.length > HISTORY_LIMIT ? back.slice(back.length - HISTORY_LIMIT) : back, forward: [] };
}

function pinnedCount(tabs: Tab[]): number {
  let n = 0;
  for (const t of tabs) if (t.pinned) n += 1;
  return n;
}

/** Inserts a new unpinned tab right after `afterId` (or at the start of the unpinned group if that tab is pinned). */
function insertTabAfter(tabs: Tab[], afterId: string, tab: Tab): Tab[] {
  const idx = tabs.findIndex((t) => t.id === afterId);
  const at = Math.max(idx + 1, pinnedCount(tabs));
  return [...tabs.slice(0, at), tab, ...tabs.slice(at)];
}

/** The pane after `paneId` in reading order (wrapping), or undefined when it is the only pane. */
function nextPane(layout: SplitNode, paneId: string): PaneNode | undefined {
  const ps = panes(layout);
  if (ps.length < 2) return undefined;
  const i = ps.findIndex((p) => p.id === paneId);
  return ps[(i + 1) % ps.length];
}

/**
 * Opens `url`:
 * - "current": navigates the active tab (pushes history; no-op if already there);
 * - "newTab": creates tab `newId` after the active tab and shows it in the focused pane;
 * - "split": creates tab `newId` in a new pane `newId` to the right of the focused pane. When the layout
 *   is already at `maxPanes`, the tab opens in the next pane instead (or the focused one if there is just one).
 */
export function openUrl(state: ShellState, url: string, target: OpenTarget, newId: string): ShellState {
  if (target === "current") return updateTab(state, state.activeTabId, (t) => navigateTab(t, url));

  const tab: Tab = { id: newId, url, back: [], forward: [], pinned: false };
  const tabs = insertTabAfter(state.tabs, state.activeTabId, tab);

  if (target === "newTab") {
    return tidy(
      {
        ...state,
        tabs,
        activeTabId: newId,
        layout: setPaneTab(state.layout, state.focusedPaneId, newId),
      },
      false,
    );
  }

  const count = panes(state.layout).length;
  if (count < state.maxPanes) {
    const layout = mapPanes(state.layout, (p) =>
      p.id === state.focusedPaneId
        ? { type: "split", split: "row", ratio: 0.5, a: p, b: { type: "pane", id: newId, tabId: newId } }
        : p,
    );
    return tidy({ ...state, tabs, layout, focusedPaneId: newId, activeTabId: newId }, false);
  }
  const target2 = nextPane(state.layout, state.focusedPaneId);
  const paneId = target2 === undefined ? state.focusedPaneId : target2.id;
  return tidy({ ...state, tabs, layout: setPaneTab(state.layout, paneId, newId), focusedPaneId: paneId, activeTabId: newId }, false);
}

// ---------- Switch to an open view ----------

/**
 * The "view" a URL shows, for switch-to-tab: two URLs with the same key are the same view in different states.
 * - a channel and its threads are one view (`channel:C`) — a thread is a place inside its channel;
 * - a page and a database (`/p/X`, `/db/X`, any `?view=`) are one view (`page:X`) — the db view is that tab's state;
 * - Settings is one view whatever the section, and so is the Huddles area; a DM, huddle, file, home, activity, agents,
 *   projects are one each;
 * - a search is only the same view with the same query; unknown URLs only when equal.
 * The key is never parsed back; ports only compare it.
 */
export function viewKey(url: string): string {
  const r = parseUrl(url);
  if (r === undefined) return `url:${url.trim()}`;
  switch (r.type) {
    case "channel":
    case "thread":
      return `channel:${r.ws}/${r.channelId}`;
    case "page":
      return `page:${r.ws}/${r.pageId}`;
    case "db":
      return `page:${r.ws}/${r.dbId}`;
    case "dm":
      return `dm:${r.ws}/${r.userId}`;
    case "huddle":
      return `huddle:${r.ws}/${r.huddleId}`;
    case "file":
      return `file:${r.ws}/${r.fileId}`;
    case "search":
      return `url:${formatUrl(r)}`;
    case "mailThread":
      return `mailThread:${r.ws}/${r.threadId}`;
    case "home":
    case "activity":
    case "later":
    case "mail":
    case "contacts":
    case "docs":
    case "calendar":
    case "unreads":
    case "scheduled":
    case "drafts":
    case "agents":
    case "projects":
    case "settings":
    // The Huddles area and its past huddles' pages are one view, like Settings; so is Files (one tab browses it).
    case "huddles":
    case "files":
      return `${r.type}:${r.ws}`;
  }
}

/** What a plain open does: open as asked, or switch to the tab `tabId` and show `url` there (no-op when it already does). */
export type OpenPlan = { kind: "open"; target: OpenTarget } | { kind: "focus"; tabId: string; url: string };

/**
 * Switch to tab (Slack / Linear / browser "switch to tab"). A "current" open of a view that is already open in this
 * window focuses it instead of navigating the active tab to a second copy. Tabs are searched in this order, and the
 * first match wins:
 * 1. the active tab showing exactly `url` (nothing to do: no history entry);
 * 2. another tab showing exactly `url` — panes on screen in reading order first, then the other tabs in strip order;
 * 3. the active tab showing the same view (`viewKey`): it navigates in place, as before (e.g. #general → a thread);
 * 4. another tab showing the same view, in the same order: it is focused and navigates to `url` (e.g. a thread link
 *    while #general is open in another tab opens the thread in that tab). Opening a database without `?view=` keeps
 *    the view that tab shows.
 * "newTab" and "split" open a new tab, except for a huddle call: there is one of each, so any open of a call that is
 * already open focuses it (as Slack and Google Meet bring the call's window forward); and a mail opened beside the list
 * takes the pane already reading mail, so mails don't stack up (only "newTab" keeps two open). `url` must be normalized
 * (`normalizeUrl`).
 */
export function resolveOpen(state: ShellState, url: string, target: OpenTarget): OpenPlan {
  const route = parseUrl(url);
  if (target === "split" && route?.type === "mailThread") {
    const reader = panes(state.layout).map((p) => findTab(state, p.tabId)).find((t) => t !== undefined && parseUrl(t.url)?.type === "mailThread");
    if (reader !== undefined) return { kind: "focus", tabId: reader.id, url };
  }
  if (target !== "current" && route?.type !== "huddle") return { kind: "open", target };
  const active = activeTab(state);
  const onScreen = panes(state.layout).map((p) => p.tabId);
  const others = [...onScreen.filter((id) => id !== active.id), ...state.tabs.map((t) => t.id).filter((id) => !onScreen.includes(id))];
  const candidates = others.map((id) => findTab(state, id)).filter((t): t is Tab => t !== undefined);
  if (active.url === url) return { kind: "focus", tabId: active.id, url };
  const exact = candidates.find((t) => t.url === url);
  if (exact !== undefined) return { kind: "focus", tabId: exact.id, url };
  const key = viewKey(url);
  const r = parseUrl(url);
  const keepView = (t: Tab) => (r?.type === "page" || (r?.type === "db" && r.view === undefined) ? t.url : url);
  if (viewKey(active.url) === key) return { kind: "focus", tabId: active.id, url: keepView(active) };
  const same = candidates.find((t) => viewKey(t.url) === key);
  if (same !== undefined) return { kind: "focus", tabId: same.id, url: keepView(same) };
  return { kind: "open", target };
}

/**
 * `openUrl` with switch-to-tab (`resolveOpen`): focuses the matching tab — `selectTab` with split groups (`groups`, the
 * `tabs` feature), `activateTab` without — and navigates it when the plan says so. Otherwise opens as asked
 * ("newTab" with split groups is `openTab`). `newId` names a new tab / pane. `url` must be normalized.
 */
export function openOrFocus(state: ShellState, url: string, target: OpenTarget, newId: string, groups: boolean): ShellState {
  const plan = resolveOpen(state, url, target);
  if (plan.kind === "open") return groups && plan.target === "newTab" ? openTab(state, url, newId) : openUrl(state, url, plan.target, newId);
  const s = plan.tabId === state.activeTabId ? state : groups ? selectTab(state, plan.tabId, newId) : activateTab(state, plan.tabId);
  return findTab(s, plan.tabId)?.url === plan.url ? s : updateTab(s, plan.tabId, (t) => navigateTab(t, plan.url));
}

/** Opens the start page in a new tab. */
export function newTab(state: ShellState, newId: string): ShellState {
  return openUrl(state, state.homeUrl, "newTab", newId);
}

/** Replaces the URL of a tab without adding a history entry (e.g. search query or db view changes). */
export function replaceUrl(state: ShellState, tabId: string, url: string): ShellState {
  if (findTab(state, tabId) === undefined) return state;
  return updateTab(state, tabId, (t) => (t.url === url ? t : { ...t, url }));
}

/** Tabs that are open but not shown in any pane. */
function hiddenTabs(state: ShellState): Tab[] {
  const shown = new Set(panes(state.layout).map((p) => p.tabId));
  return state.tabs.filter((t) => !shown.has(t.id));
}

/**
 * Closes a tab. If it was shown in a pane, that pane shows the nearest hidden tab (right first, then left);
 * if there is none, the pane closes (when other panes exist). Closing the only tab resets it to the start page.
 */
export function closeTab(state: ShellState, tabId: string): ShellState {
  const idx = state.tabs.findIndex((t) => t.id === tabId);
  if (idx < 0) return state;
  if (state.tabs.length === 1) {
    return updateTab(state, tabId, (t) => ({ ...t, url: state.homeUrl, back: [], forward: [], pinned: false }));
  }
  const pane = paneOfTab(state, tabId);
  const tabs = state.tabs.filter((t) => t.id !== tabId);
  if (pane === undefined) return tidy({ ...state, tabs }, false);

  const hidden = new Set(hiddenTabs(state).map((t) => t.id));
  let replacement: Tab | undefined;
  for (let i = idx + 1; i < state.tabs.length && replacement === undefined; i++) {
    const t = state.tabs[i]!;
    if (hidden.has(t.id)) replacement = t;
  }
  for (let i = idx - 1; i >= 0 && replacement === undefined; i--) {
    const t = state.tabs[i]!;
    if (hidden.has(t.id)) replacement = t;
  }

  if (replacement !== undefined) {
    const next: ShellState = { ...state, tabs, layout: setPaneTab(state.layout, pane.id, replacement.id) };
    return tidy(pane.id === state.focusedPaneId ? { ...next, activeTabId: replacement.id } : next, false);
  }
  // No hidden tab to show: collapse the pane (there must be another pane, since every pane shows a distinct tab).
  const layout = removePane(state.layout, pane.id);
  if (layout === undefined) return state;
  const next: ShellState = { ...state, tabs, layout };
  if (pane.id !== state.focusedPaneId) return tidy(next, false);
  return tidy(focusSiblingOf(next, state.layout, pane.id), false);
}

/** After removing `removedId` from `oldLayout`, focus the first pane of its former sibling subtree. */
function focusSiblingOf(state: ShellState, oldLayout: SplitNode, removedId: string): ShellState {
  const sibling = siblingOf(oldLayout, removedId);
  const target = sibling === undefined ? panes(state.layout)[0] : panes(sibling)[0];
  return target === undefined ? state : focus(state, target.id);
}

function siblingOf(node: SplitNode, paneId: string): SplitNode | undefined {
  if (node.type === "pane") return undefined;
  if (node.a.type === "pane" && node.a.id === paneId) return node.b;
  if (node.b.type === "pane" && node.b.id === paneId) return node.a;
  return siblingOf(node.a, paneId) ?? siblingOf(node.b, paneId);
}

/** Shows a tab: focuses the pane already showing it, otherwise shows it in the focused pane. */
export function activateTab(state: ShellState, tabId: string): ShellState {
  if (findTab(state, tabId) === undefined) return state;
  const pane = paneOfTab(state, tabId);
  if (pane !== undefined) return focus(state, pane.id);
  return tidy({ ...state, activeTabId: tabId, layout: setPaneTab(state.layout, state.focusedPaneId, tabId) }, false);
}

export function goBack(state: ShellState): ShellState {
  const t = activeTab(state);
  const prev = t.back[t.back.length - 1];
  if (prev === undefined) return state;
  return updateTab(state, t.id, (x) => ({ ...x, url: prev, back: x.back.slice(0, -1), forward: [x.url, ...x.forward] }));
}

export function goForward(state: ShellState): ShellState {
  const t = activeTab(state);
  const next = t.forward[0];
  if (next === undefined) return state;
  return updateTab(state, t.id, (x) => ({ ...x, url: next, back: [...x.back, x.url], forward: x.forward.slice(1) }));
}

/** Splits the focused pane; the new pane `newId` shows a new start-page tab `newId`. No-op at maxPanes. */
export function splitFocused(state: ShellState, direction: SplitDirection, newId: string): ShellState {
  if (panes(state.layout).length >= state.maxPanes) return state;
  const tab: Tab = { id: newId, url: state.homeUrl, back: [], forward: [], pinned: false };
  const layout = mapPanes(state.layout, (p) =>
    p.id === state.focusedPaneId
      ? { type: "split", split: direction, ratio: 0.5, a: p, b: { type: "pane", id: newId, tabId: newId } }
      : p,
  );
  return tidy(
    {
      ...state,
      tabs: insertTabAfter(state.tabs, state.activeTabId, tab),
      layout,
      focusedPaneId: newId,
      activeTabId: newId,
    },
    false,
  );
}

/** Closes a pane (its tab stays open, hidden). No-op for the only pane. */
export function closePane(state: ShellState, paneId: string): ShellState {
  const layout = removePane(state.layout, paneId);
  if (layout === undefined || layout === state.layout) return state;
  const next: ShellState = { ...state, layout };
  if (paneId !== state.focusedPaneId) return next;
  return focusSiblingOf(next, state.layout, paneId);
}

/**
 * Sets the ratio of the split at `path` ("" = root, then "a"/"b" per level, e.g. "b" = root's second child).
 * The ratio is clamped to [MIN_RATIO, MAX_RATIO]. Invalid paths are a no-op.
 */
export function setRatio(state: ShellState, path: string, ratio: number): ShellState {
  const layout = setRatioAt(state.layout, path, clampRatio(ratio));
  return layout === undefined ? state : { ...state, layout };
}

function setRatioAt(node: SplitNode, path: string, ratio: number): SplitNode | undefined {
  if (node.type === "pane") return undefined;
  if (path === "") return { ...node, ratio };
  const head = path.charAt(0);
  const rest = path.slice(1);
  if (head === "a") {
    const a = setRatioAt(node.a, rest, ratio);
    return a === undefined ? undefined : { ...node, a };
  }
  if (head === "b") {
    const b = setRatioAt(node.b, rest, ratio);
    return b === undefined ? undefined : { ...node, b };
  }
  return undefined;
}

/** Pins (moves to the end of the pinned group) or unpins (moves to the start of the unpinned group). */
export function pinTab(state: ShellState, tabId: string, pinned: boolean): ShellState {
  const tab = findTab(state, tabId);
  if (tab === undefined || tab.pinned === pinned) return state;
  const others = state.tabs.filter((t) => t.id !== tabId);
  const at = pinnedCount(others);
  return tidy({ ...state, tabs: [...others.slice(0, at), { ...tab, pinned }, ...others.slice(at)] }, false);
}

/** Moves a tab to `toIndex`, clamped so pinned tabs stay in the pinned group and unpinned ones after it. */
export function moveTab(state: ShellState, tabId: string, toIndex: number): ShellState {
  const tab = findTab(state, tabId);
  if (tab === undefined) return state;
  const others = state.tabs.filter((t) => t.id !== tabId);
  const pins = pinnedCount(others);
  const lo = tab.pinned ? 0 : pins;
  const hi = tab.pinned ? pins : others.length;
  const at = Math.max(lo, Math.min(hi, Math.floor(toIndex)));
  return tidy({ ...state, tabs: [...others.slice(0, at), tab, ...others.slice(at)] }, false);
}

/**
 * Changes the pane limit (desktop 4, tablet 2, phone 1). When shrinking, panes are closed from the end of
 * the reading order, never the focused one; their tabs stay open. Background splits keep their panes until they come
 * back on screen (a narrow window for a moment does not take them apart).
 */
export function setMaxPanes(state: ShellState, maxPanes: number): ShellState {
  const max = clampPanes(maxPanes);
  let next: ShellState = { ...state, maxPanes: max };
  for (;;) {
    const ps = panes(next.layout);
    if (ps.length <= max) return next;
    let victim: PaneNode | undefined;
    for (let i = ps.length - 1; i >= 0 && victim === undefined; i--) {
      if (ps[i]!.id !== next.focusedPaneId) victim = ps[i];
    }
    if (victim === undefined) return next;
    next = closePane(next, victim.id);
  }
}

/** Closes panes from the end of the reading order, never `keepPaneId`, until at most `max` remain. */
function fitLayout(layout: SplitNode, max: number, keepPaneId: string): SplitNode {
  let node = layout;
  for (;;) {
    const ps = panes(node);
    let victim: PaneNode | undefined;
    for (let i = ps.length - 1; i >= 0 && victim === undefined; i--) if (ps[i]!.id !== keepPaneId) victim = ps[i];
    if (ps.length <= max || victim === undefined) return node;
    const rest = removePane(node, victim.id);
    if (rest === undefined) return node;
    node = rest;
  }
}

export function focusPane(state: ShellState, paneId: string): ShellState {
  return focus(state, paneId);
}

/** Checks the invariants; returns a list of problems (empty = valid). Used to validate restored state. */
export function validate(state: ShellState): string[] {
  const problems: string[] = [];
  const ids = new Set<string>();
  for (const t of state.tabs) {
    if (ids.has(t.id)) problems.push(`duplicate tab ${t.id}`);
    ids.add(t.id);
  }
  if (state.tabs.length === 0) problems.push("no tabs");
  const ps = panes(state.layout);
  const shown = new Set<string>();
  const paneIds = new Set<string>();
  for (const p of ps) {
    if (!ids.has(p.tabId)) problems.push(`pane ${p.id} shows missing tab ${p.tabId}`);
    if (shown.has(p.tabId)) problems.push(`tab ${p.tabId} shown twice`);
    if (paneIds.has(p.id)) problems.push(`duplicate pane ${p.id}`);
    shown.add(p.tabId);
    paneIds.add(p.id);
  }
  const fp = ps.find((p) => p.id === state.focusedPaneId);
  if (fp === undefined) problems.push("focused pane missing");
  else if (fp.tabId !== state.activeTabId) problems.push("active tab is not the focused pane's tab");
  if (ps.length > state.maxPanes) problems.push("too many panes");
  let seenUnpinned = false;
  for (const t of state.tabs) {
    if (!t.pinned) seenUnpinned = true;
    else if (seenUnpinned) problems.push(`pinned tab ${t.id} after unpinned`);
  }
  for (const l of state.splits) {
    const bs = panes(l);
    const first = bs[0]!.id;
    if (bs.length < 2) problems.push(`background split ${first} has one pane`);
    for (const p of bs) {
      if (!ids.has(p.tabId)) problems.push(`pane ${p.id} shows missing tab ${p.tabId}`);
      if (shown.has(p.tabId)) problems.push(`tab ${p.tabId} shown twice`);
      if (paneIds.has(p.id)) problems.push(`duplicate pane ${p.id}`);
      shown.add(p.tabId);
      paneIds.add(p.id);
    }
  }
  const order = new Map(state.tabs.map((t, i) => [t.id, i]));
  const groupIds = new Set<string>();
  const grouped = new Set<string>();
  for (const g of state.groups) {
    if (groupIds.has(g.id)) problems.push(`duplicate group ${g.id}`);
    groupIds.add(g.id);
    if (g.tabIds.length === 0) problems.push(`group ${g.id} is empty`);
    const at: number[] = [];
    for (const id of g.tabIds) {
      const i = order.get(id);
      if (i === undefined) problems.push(`group ${g.id} has missing tab ${id}`);
      else {
        at.push(i);
        if (state.tabs[i]!.pinned) problems.push(`group ${g.id} has pinned tab ${id}`);
      }
      if (grouped.has(id)) problems.push(`tab ${id} in two groups`);
      grouped.add(id);
    }
    for (let k = 1; k < at.length; k++) {
      if (at[k] !== at[k - 1]! + 1) {
        problems.push(`group ${g.id} is not contiguous`);
        break;
      }
    }
  }
  return problems;
}

// ---------- Split groups and tab groups ----------

/** Where `splitPane` puts the new pane, relative to the target pane. */
export type SplitSide = "left" | "right" | "top" | "bottom";

/**
 * One entry of the tab strip: a lone tab, or the tabs of one split view (a joined tab).
 * `id` is the tab's id, or `split:` + the split's first tab (in strip order) for a split view.
 */
export type TabUnit = {
  id: string;
  /** The lone tab, or a split view's tabs in pane reading order (left/top first). */
  tabIds: string[];
  split: boolean;
  pinned: boolean;
  /** The tab group the unit is in. */
  groupId?: string;
  /** On screen. */
  shown: boolean;
};

function hasTab(layout: SplitNode, tabId: string): boolean {
  return panes(layout).some((p) => p.tabId === tabId);
}

function sameItems<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** Moves the tabs in `ids` next to each other, where the first of them is (others keep their order). */
function gatherTabs(tabs: Tab[], ids: Set<string>): Tab[] {
  const first = tabs.findIndex((t) => ids.has(t.id));
  if (first < 0) return tabs;
  const members = tabs.filter((t) => ids.has(t.id));
  let together = true;
  for (let i = 0; i < members.length; i++) if (tabs[first + i] !== members[i]) together = false;
  if (together) return tabs;
  const rest = tabs.filter((t) => !ids.has(t.id));
  return [...rest.slice(0, first), ...members, ...rest.slice(first)];
}

function pinnedFirst(tabs: Tab[]): Tab[] {
  const pins = tabs.filter((t) => t.pinned);
  const out = [...pins, ...tabs.filter((t) => !t.pinned)];
  return sameItems(out, tabs) ? tabs : out;
}

/** Group membership: every tab in `ids` leaves its group, then joins `groupId` (when given). */
function regroup(groups: TabGroup[], ids: string[], groupId: string | undefined): TabGroup[] {
  const set = new Set(ids);
  return groups.map((g) => {
    const rest = g.tabIds.filter((id) => !set.has(id));
    if (g.id === groupId) return { ...g, tabIds: [...rest, ...ids] };
    return rest.length === g.tabIds.length ? g : { ...g, tabIds: rest };
  });
}

function groupOfTab(groups: TabGroup[], tabId: string): TabGroup | undefined {
  return groups.find((g) => g.tabIds.includes(tabId));
}

/**
 * Restores the group and background-split invariants after an op: background splits keep only existing tabs that no
 * other layout shows (one pane left → a plain tab); groups keep existing unpinned tabs, side by side; the closed
 * stack keeps the last CLOSED_LIMIT entries. With `gather` (split-group mode) the tabs of each split view — the one on
 * screen included — also take the pin section and group of their first tab and sit side by side.
 */
function tidy(state: ShellState, gather: boolean): ShellState {
  const closed = state.closed.length > CLOSED_LIMIT ? state.closed.slice(state.closed.length - CLOSED_LIMIT) : state.closed;
  const multi = panes(state.layout).length > 1;
  if (state.groups.length === 0 && state.splits.length === 0 && !(gather && multi)) {
    return closed === state.closed ? state : { ...state, closed };
  }
  const exists = new Set(state.tabs.map((t) => t.id));
  const usedTabs = new Set(panes(state.layout).map((p) => p.tabId));
  const usedPanes = new Set(panes(state.layout).map((p) => p.id));
  const splits: SplitNode[] = [];
  for (const l of state.splits) {
    let node: SplitNode | undefined = l;
    for (const p of panes(l)) {
      if (node !== undefined && (!exists.has(p.tabId) || usedTabs.has(p.tabId) || usedPanes.has(p.id))) node = removePane(node, p.id);
    }
    if (node === undefined || node.type === "pane") continue;
    for (const p of panes(node)) {
      usedTabs.add(p.tabId);
      usedPanes.add(p.id);
    }
    splits.push(node);
  }

  let tabs = state.tabs;
  let groups = state.groups;
  if (gather) {
    for (const l of multi ? [state.layout, ...splits] : splits) {
      const ids = panes(l).map((p) => p.tabId);
      const set = new Set(ids);
      const anchor = tabs.find((t) => set.has(t.id));
      if (anchor === undefined) continue;
      const members = tabs.filter((t) => set.has(t.id)).map((t) => t.id);
      if (tabs.some((t) => set.has(t.id) && t.pinned !== anchor.pinned)) {
        tabs = tabs.map((t) => (set.has(t.id) && t.pinned !== anchor.pinned ? { ...t, pinned: anchor.pinned } : t));
      }
      const g = anchor.pinned ? undefined : groupOfTab(groups, anchor.id);
      if (groups.some((x) => x.id !== g?.id && x.tabIds.some((id) => set.has(id))) || (g !== undefined && !members.every((id) => g.tabIds.includes(id)))) {
        groups = regroup(groups, members, g?.id);
      }
      tabs = gatherTabs(tabs, set);
    }
  }

  // Groups: existing unpinned tabs, each in one group, side by side.
  const pinned = new Set(tabs.filter((t) => t.pinned).map((t) => t.id));
  const open = new Set(tabs.map((t) => t.id));
  const seen = new Set<string>();
  const kept: TabGroup[] = [];
  for (const g of groups) {
    const ids: string[] = [];
    for (const id of g.tabIds) {
      if (!open.has(id) || pinned.has(id) || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
    if (ids.length > 0) kept.push(sameItems(ids, g.tabIds) ? g : { ...g, tabIds: ids });
  }
  for (let round = 0; round <= kept.length; round++) {
    const before = tabs;
    for (const g of kept) tabs = gatherTabs(tabs, new Set(g.tabIds));
    if (before === tabs) break;
  }
  tabs = pinnedFirst(tabs);
  const index = new Map(tabs.map((t, i) => [t.id, i]));
  const sorted = kept
    .map((g) => {
      const ids = g.tabIds.toSorted((a, b) => index.get(a)! - index.get(b)!);
      return sameItems(ids, g.tabIds) ? g : { ...g, tabIds: ids };
    })
    .toSorted((a, b) => index.get(a.tabIds[0]!)! - index.get(b.tabIds[0]!)!);

  if (sameItems(tabs, state.tabs) && sameItems(sorted, state.groups) && sameItems(splits, state.splits) && closed === state.closed) return state;
  return { ...state, tabs, groups: sorted, splits, closed };
}

/**
 * Split-group mode's normal form (see `tidy`): each split view's tabs side by side, in the pin section and group of
 * the first one. The view model applies it after every op when the `tabs` feature is on.
 */
export function tidyTabs(state: ShellState): ShellState {
  return tidy(state, true);
}

/** Loads persisted state from any version: fills what older builds did not write and drops dangling references. */
export function migrateState(stored: StoredShellState): ShellState {
  return tidy(
    {
      tabs: stored.tabs,
      activeTabId: stored.activeTabId,
      layout: stored.layout,
      focusedPaneId: stored.focusedPaneId,
      maxPanes: stored.maxPanes,
      homeUrl: stored.homeUrl,
      groups: stored.groups ?? [],
      splits: stored.splits ?? [],
      closed: stored.closed ?? [],
    },
    false,
  );
}

/** The tabs of the split view showing `tabId` (on screen or not), in reading order; `[tabId]` for a lone tab. */
export function splitMembers(state: ShellState, tabId: string): string[] {
  for (const l of [state.layout, ...state.splits]) {
    const ps = panes(l);
    if (ps.some((p) => p.tabId === tabId)) return ps.length > 1 ? ps.map((p) => p.tabId) : [tabId];
  }
  return findTab(state, tabId) === undefined ? [] : [tabId];
}

/** The strip's entries in order: lone tabs and split views (each split view is one entry). */
export function tabUnits(state: ShellState): TabUnit[] {
  const shown = new Set(panes(state.layout).map((p) => p.tabId));
  const splitOf = new Map<string, string[]>();
  for (const l of [state.layout, ...state.splits]) {
    const ids = panes(l).map((p) => p.tabId);
    if (ids.length > 1) for (const id of ids) splitOf.set(id, ids);
  }
  const groupOf = new Map<string, string>();
  for (const g of state.groups) for (const id of g.tabIds) groupOf.set(id, g.id);
  const done = new Set<string>();
  const out: TabUnit[] = [];
  for (const t of state.tabs) {
    if (done.has(t.id)) continue;
    const members = splitOf.get(t.id);
    const ids = members ?? [t.id];
    for (const id of ids) done.add(id);
    const unit: TabUnit = { id: members === undefined ? t.id : `split:${t.id}`, tabIds: ids, split: members !== undefined, pinned: t.pinned, shown: ids.some((id) => shown.has(id)) };
    const g = groupOf.get(t.id);
    if (g !== undefined) unit.groupId = g;
    out.push(unit);
  }
  return out;
}

/** A colour no group uses yet (palette order), or the palette again from the start. */
export function nextGroupColor(groups: TabGroup[]): GroupColor {
  for (const c of GROUP_COLORS) if (!groups.some((g) => g.color === c)) return c;
  return GROUP_COLORS[groups.length % GROUP_COLORS.length]!;
}

function cleanName(name: string): string {
  return name.trim().replace(/\s+/g, " ").slice(0, GROUP_NAME_MAX);
}

/** The given tabs plus their split partners, in `tabs` order (unknown ids dropped). */
function withPartners(state: ShellState, tabIds: string[]): string[] {
  const set = new Set<string>();
  for (const id of tabIds) for (const m of splitMembers(state, id)) set.add(m);
  return state.tabs.filter((t) => set.has(t.id)).map((t) => t.id);
}

/**
 * Shows `tabId`: its split view if it is in one, else the tab alone. `keep`: the layout on screen stays (a split goes
 * to the background); otherwise it is gone (its tabs closed). A fresh pane is `newId`.
 */
function present(state: ShellState, tabId: string, newId: string, keep: boolean): ShellState {
  const at = state.splits.findIndex((l) => hasTab(l, tabId));
  const multi = panes(state.layout).length > 1;
  const splits = state.splits.filter((_, i) => i !== at);
  if (keep && multi) splits.push(state.layout);
  const parked = state.splits[at];
  if (parked !== undefined) {
    // Fitted to the pane limit (a phone shows the tab alone; the other tabs of the split stay open).
    const p = panes(parked).find((x) => x.tabId === tabId)!;
    return { ...state, splits, layout: fitLayout(parked, state.maxPanes, p.id), focusedPaneId: p.id, activeTabId: tabId };
  }
  const paneId = keep && !multi ? state.focusedPaneId : newId;
  return { ...state, splits, layout: { type: "pane", id: paneId, tabId }, focusedPaneId: paneId, activeTabId: tabId };
}

/**
 * Selects a tab (split-group mode): focuses its pane when it is on screen, brings back its split view when it is in
 * one, or shows it alone — a split view on screen stays together in the background. A collapsed group holding the tab
 * expands. `newId` names the pane a lone tab gets when a split view leaves the screen.
 */
export function selectTab(state: ShellState, tabId: string, newId: string): ShellState {
  if (findTab(state, tabId) === undefined) return state;
  const g = groupOfTab(state.groups, tabId);
  const s = g !== undefined && g.collapsed ? { ...state, groups: state.groups.map((x) => (x.id === g.id ? { ...x, collapsed: false } : x)) } : state;
  const pane = paneOfTab(s, tabId);
  if (pane !== undefined) return focus(s, pane.id);
  return tidy(present(s, tabId, newId, true), true);
}

/** Index in `tabs` right after the unit of `tabId` (and never inside the pinned section for unpinned tabs). */
function afterUnit(state: ShellState, tabId: string): number {
  const ids = new Set(splitMembers(state, tabId));
  let last = -1;
  state.tabs.forEach((t, i) => {
    if (ids.has(t.id)) last = i;
  });
  return last + 1;
}

/**
 * Opens `url` in a new tab of its own (split-group mode's "new tab"): tab `newId` goes right after the active tab's
 * entry and into its group, and is shown alone; a split view on screen stays together in the background.
 */
export function openTab(state: ShellState, url: string, newId: string): ShellState {
  const active = activeTab(state);
  const tab: Tab = { id: newId, url, back: [], forward: [], pinned: false };
  const at = active.pinned ? pinnedCount(state.tabs) : afterUnit(state, active.id);
  const tabs = [...state.tabs.slice(0, at), tab, ...state.tabs.slice(at)];
  const g = active.pinned ? undefined : groupOfTab(state.groups, active.id);
  const groups = g === undefined ? state.groups : regroup(state.groups, [newId], g.id);
  return selectTab({ ...state, tabs, groups }, newId, newId);
}

/** "Duplicate": tab `newId` with the same URL and history, right after the tab's entry, in its group; selected. */
export function duplicateTab(state: ShellState, tabId: string, newId: string): ShellState {
  const tab = findTab(state, tabId);
  if (tab === undefined) return state;
  const at = afterUnit(state, tabId);
  const tabs = [...state.tabs.slice(0, at), { ...tab, id: newId }, ...state.tabs.slice(at)];
  const g = groupOfTab(state.groups, tabId);
  const groups = g === undefined ? state.groups : regroup(state.groups, [newId], g.id);
  return selectTab({ ...state, tabs, groups }, newId, newId);
}

/** Closed-stack entries for closing `ids`: one per run of neighbouring tabs from the same group (or none). */
function closedEntries(state: ShellState, ids: Set<string>): ClosedEntry[] {
  const runs: { tabs: Tab[]; index: number; groupId: string | undefined }[] = [];
  let kept = 0;
  let run: { tabs: Tab[]; index: number; groupId: string | undefined } | undefined;
  for (const t of state.tabs) {
    if (!ids.has(t.id)) {
      run = undefined;
      kept += 1;
      continue;
    }
    const groupId = groupOfTab(state.groups, t.id)?.id;
    if (run !== undefined && run.groupId === groupId) run.tabs.push(t);
    else {
      run = { tabs: [t], index: kept, groupId };
      runs.push(run);
    }
  }
  const layouts = panes(state.layout).length > 1 ? [state.layout, ...state.splits] : state.splits;
  return runs.map((r) => {
    const inRun = new Set(r.tabs.map((t) => t.id));
    const entry: ClosedEntry = { tabs: r.tabs, index: r.index, splits: layouts.filter((l) => panes(l).every((p) => inRun.has(p.tabId))) };
    const g = r.groupId === undefined ? undefined : state.groups.find((x) => x.id === r.groupId);
    if (g !== undefined) entry.group = { id: g.id, name: g.name, color: g.color };
    return entry;
  });
}

/** The tab to show after the screen's tabs closed: the nearest open one right of `from`, then left; tabs in collapsed groups last. */
function nearestOpen(tabs: Tab[], from: number, gone: Set<string>, groups: TabGroup[]): string | undefined {
  const hidden = new Set<string>();
  for (const g of groups) if (g.collapsed) for (const id of g.tabIds) hidden.add(id);
  const order: Tab[] = [...tabs.slice(from + 1), ...tabs.slice(0, Math.max(0, from)).toReversed()];
  const open = order.filter((t) => !gone.has(t.id));
  return (open.find((t) => !hidden.has(t.id)) ?? open[0])?.id;
}

/**
 * Closes tabs (split-group mode): ⌘W, "Close others", "Close to the right", a group or a split view. Each close is
 * pushed to `closed` for ⌘⇧T. Panes of closed tabs leave their split (one pane left → a normal tab); when nothing on
 * screen remains, the nearest tab (right first) is selected. Closing every tab leaves a fresh start page `newId`.
 */
export function closeTabs(state: ShellState, tabIds: string[], newId: string): ShellState {
  const gone = new Set(tabIds.filter((id) => findTab(state, id) !== undefined));
  if (gone.size === 0) return state;
  const closed = [...state.closed, ...closedEntries(state, gone)];
  const tabs = state.tabs.filter((t) => !gone.has(t.id));
  if (tabs.length === 0) {
    const home: Tab = { id: newId, url: state.homeUrl, back: [], forward: [], pinned: false };
    return tidy({ ...state, tabs: [home], activeTabId: newId, layout: { type: "pane", id: newId, tabId: newId }, focusedPaneId: newId, groups: [], splits: [], closed }, true);
  }
  const without = (l: SplitNode): SplitNode | undefined => {
    let node: SplitNode | undefined = l;
    for (const p of panes(l)) if (node !== undefined && gone.has(p.tabId)) node = removePane(node, p.id);
    return node;
  };
  const layout = without(state.layout);
  const splits: SplitNode[] = [];
  for (const l of state.splits) {
    const node = without(l);
    if (node !== undefined && node.type === "split") splits.push(node);
  }
  const next: ShellState = { ...state, tabs, closed, splits };
  if (layout !== undefined) {
    const before = panes(state.layout);
    const left = new Set(panes(layout).map((p) => p.id));
    const i = before.findIndex((p) => p.id === state.focusedPaneId);
    const order = [...before.slice(i), ...before.slice(0, Math.max(0, i)).toReversed()];
    const target = order.find((p) => left.has(p.id))!;
    return tidy({ ...next, layout, focusedPaneId: target.id, activeTabId: target.tabId }, true);
  }
  const from = state.tabs.findIndex((t) => t.id === state.activeTabId);
  const pick = nearestOpen(state.tabs, from, gone, state.groups)!;
  return tidy(present(next, pick, newId, false), true);
}

/** "Close others": unpinned tabs outside the units of `keepTabIds`. */
export function otherTabs(state: ShellState, keepTabIds: string[]): string[] {
  const keep = new Set(withPartners(state, keepTabIds));
  return state.tabs.filter((t) => !t.pinned && !keep.has(t.id)).map((t) => t.id);
}

/** "Close to the right": unpinned tabs after the unit of `tabId`. */
export function tabsToTheRight(state: ShellState, tabId: string): string[] {
  if (findTab(state, tabId) === undefined) return [];
  return state.tabs.slice(afterUnit(state, tabId)).filter((t) => !t.pinned).map((t) => t.id);
}

/**
 * ⌘⇧T: reopens the last closed entry where it was — back in its group (recreated if it has gone) and its split
 * views — and selects its first tab. Tabs that are open again already are skipped.
 */
export function reopenClosed(state: ShellState, newId: string): ShellState {
  const entry = state.closed[state.closed.length - 1];
  if (entry === undefined) return state;
  const closed = state.closed.slice(0, -1);
  const open = new Set(state.tabs.map((t) => t.id));
  const back = entry.tabs.filter((t) => !open.has(t.id));
  const first = back[0];
  if (first === undefined) return { ...state, closed };
  const at = Math.max(0, Math.min(state.tabs.length, Math.floor(entry.index)));
  const tabs = [...state.tabs.slice(0, at), ...back, ...state.tabs.slice(at)];
  const ids = back.filter((t) => !t.pinned).map((t) => t.id);
  let groups = state.groups;
  const g = entry.group;
  if (g !== undefined && ids.length > 0) {
    groups = groups.some((x) => x.id === g.id)
      ? regroup(groups, ids, g.id)
      : [...groups, { id: g.id, name: g.name, color: g.color, collapsed: false, tabIds: ids }];
  }
  const restored = new Set(back.map((t) => t.id));
  const paneIds = new Set([state.layout, ...state.splits].flatMap((l) => panes(l).map((p) => p.id)));
  const splits = [...state.splits];
  for (const l of entry.splits) {
    const ps = panes(l);
    if (ps.every((p) => restored.has(p.tabId) && !paneIds.has(p.id))) {
      splits.push(l);
      for (const p of ps) paneIds.add(p.id);
    }
  }
  return selectTab(tidy({ ...state, tabs, groups, splits, closed }, true), first.id, newId);
}

/**
 * Moves tab `tabId` into a new pane `newId` beside pane `paneId` of the layout on screen, making one split view: the
 * tab leaves the pane or split it was in and joins the target's pin section and group, right after it. No-op when the
 * layout is full, the pane is missing or already shows the tab.
 */
export function splitPane(state: ShellState, paneId: string, tabId: string, side: SplitSide, newId: string): ShellState {
  const tab = findTab(state, tabId);
  const target = panes(state.layout).find((p) => p.id === paneId);
  if (tab === undefined || target === undefined || target.tabId === tabId) return state;
  const own = paneOfTab(state, tabId);
  let layout = own === undefined ? state.layout : (removePane(state.layout, own.id) ?? state.layout);
  if (panes(layout).length >= state.maxPanes) return state;
  const splits = state.splits.map((l) => {
    const p = panes(l).find((x) => x.tabId === tabId);
    return p === undefined ? l : (removePane(l, p.id) ?? l);
  });
  const fresh: PaneNode = { type: "pane", id: newId, tabId };
  const before = side === "left" || side === "top";
  const split: SplitDirection = side === "left" || side === "right" ? "row" : "column";
  layout = mapPanes(layout, (p) => (p.id === paneId ? { type: "split", split, ratio: 0.5, a: before ? fresh : p, b: before ? p : fresh } : p));

  const unit = new Set(panes(layout).map((p) => p.tabId));
  unit.delete(tabId);
  const others = state.tabs.filter((t) => t.id !== tabId);
  let last = -1;
  others.forEach((t, i) => {
    if (unit.has(t.id)) last = i;
  });
  const anchor = findTab(state, target.tabId)!;
  const tabs = [...others.slice(0, last + 1), { ...tab, pinned: anchor.pinned }, ...others.slice(last + 1)];
  const g = anchor.pinned ? undefined : groupOfTab(state.groups, anchor.id);
  const groups = regroup(state.groups, [tabId], g?.id);
  return tidy({ ...state, tabs, groups, splits, layout, focusedPaneId: newId, activeTabId: tabId }, true);
}

/** "Separate tabs": the split view holding `tabId` becomes separate tabs (in place); on screen, the focused one stays. */
export function separateSplit(state: ShellState, tabId: string): ShellState {
  if (panes(state.layout).length > 1 && hasTab(state.layout, tabId)) {
    const p = panes(state.layout).find((x) => x.id === state.focusedPaneId)!;
    return tidy({ ...state, layout: p }, true);
  }
  const at = state.splits.findIndex((l) => hasTab(l, tabId));
  if (at < 0) return state;
  return tidy({ ...state, splits: state.splits.filter((_, i) => i !== at) }, true);
}

function flip(l: SplitNode): SplitNode {
  return l.type === "split" ? { ...l, a: l.b, b: l.a, ratio: clampRatio(1 - l.ratio) } : l;
}

/** "Swap panes": mirrors the outer split of the split view holding `tabId`. */
export function swapSplit(state: ShellState, tabId: string): ShellState {
  if (state.layout.type === "split" && hasTab(state.layout, tabId)) return { ...state, layout: flip(state.layout) };
  const at = state.splits.findIndex((l) => hasTab(l, tabId));
  if (at < 0) return state;
  return { ...state, splits: state.splits.map((l, i) => (i === at ? flip(l) : l)) };
}

/** Mirrors the split whose two children are exactly panes `a` and `b`; undefined when they are not siblings. */
function flipSiblings(node: SplitNode, a: string, b: string): SplitNode | undefined {
  if (node.type === "pane") return undefined;
  if (node.a.type === "pane" && node.b.type === "pane") {
    const ids = [node.a.id, node.b.id];
    if (ids.includes(a) && ids.includes(b)) return flip(node);
  }
  const x = flipSiblings(node.a, a, b);
  if (x !== undefined) return { ...node, a: x };
  const y = flipSiblings(node.b, a, b);
  return y === undefined ? undefined : { ...node, b: y };
}

/** Swaps two pane nodes (ids and tabs travel together); two siblings mirror their split instead. */
function swapNodes(node: SplitNode, a: PaneNode, b: PaneNode): SplitNode {
  return flipSiblings(node, a.id, b.id) ?? mapPanes(node, (p) => (p.id === a.id ? b : p.id === b.id ? a : p));
}

/**
 * Drag one segment of a split tab onto another ("swap sides"): tabs `tabA` and `tabB` of the same split view trade
 * places. Two sibling panes mirror their split (sizes follow the panes, like `swapSplit`); otherwise the two panes trade
 * positions and the layout's sizes stay. Focus stays with the focused tab. No-op unless both are in one split view.
 */
export function swapPanes(state: ShellState, tabA: string, tabB: string): ShellState {
  if (tabA === tabB) return state;
  const swapIn = (l: SplitNode): SplitNode | undefined => {
    const ps = panes(l);
    const pa = ps.find((p) => p.tabId === tabA);
    const pb = ps.find((p) => p.tabId === tabB);
    return pa === undefined || pb === undefined ? undefined : swapNodes(l, pa, pb);
  };
  const shown = swapIn(state.layout);
  if (shown !== undefined) return { ...state, layout: shown };
  const at = state.splits.findIndex((l) => swapIn(l) !== undefined);
  if (at < 0) return state;
  return { ...state, splits: state.splits.map((l, i) => (i === at ? swapIn(l)! : l)) };
}

/**
 * The ✕ on one segment of a split tab (or middle click / "Close tab" on it): closes that tab only. Its split partner
 * (with two panes, now a normal tab) is focused — brought on screen when the split was in the background. A tab that
 * is not in a split closes as with `closeTabs`. `newId` names a fresh pane or start page.
 */
export function closeSplitSide(state: ShellState, tabId: string, newId: string): ShellState {
  const members = splitMembers(state, tabId);
  const next = closeTabs(state, [tabId], newId);
  if (members.length < 2 || hasTab(state.layout, tabId)) return next;
  const i = members.indexOf(tabId);
  return selectTab(next, members[i + 1] ?? members[i - 1]!, newId);
}

/**
 * "Move to new tab" on one segment of a split tab: that tab leaves the split view and becomes its own entry, right after
 * the split's, and is shown alone and focused. The rest of the split view stays together in the background (one tab
 * left → a normal tab). Pin section and group are kept. No-op for a tab that is not in a split. `newId` names the pane a
 * tab from a background split gets.
 */
export function moveToNewTab(state: ShellState, tabId: string, newId: string): ShellState {
  const members = splitMembers(state, tabId);
  if (members.length < 2) return state;
  const tab = findTab(state, tabId)!;
  const others = state.tabs.filter((t) => t.id !== tabId);
  let last = -1;
  others.forEach((t, i) => {
    if (members.includes(t.id)) last = i;
  });
  const tabs = [...others.slice(0, last + 1), tab, ...others.slice(last + 1)];
  const own = paneOfTab(state, tabId);
  if (own !== undefined) {
    const rest = removePane(state.layout, own.id)!;
    const splits = rest.type === "split" ? [...state.splits, rest] : state.splits;
    return tidy({ ...state, tabs, splits, layout: own, focusedPaneId: own.id, activeTabId: tabId }, true);
  }
  const splits = state.splits.map((l) => {
    const p = panes(l).find((x) => x.tabId === tabId);
    return p === undefined ? l : (removePane(l, p.id) ?? l);
  });
  return selectTab(tidy({ ...state, tabs, splits }, true), tabId, newId);
}

/** Pins or unpins the entry of `tabId` (its whole split view): pinned → end of the pinned tabs (leaving its group); unpinned → start of the others. */
export function pinUnit(state: ShellState, tabId: string, pinned: boolean): ShellState {
  const ids = new Set(splitMembers(state, tabId));
  const block = state.tabs.filter((t) => ids.has(t.id));
  if (block.every((t) => t.pinned === pinned)) return state;
  const others = state.tabs.filter((t) => !ids.has(t.id));
  const at = pinnedCount(others);
  const moved = block.map((t) => (t.pinned === pinned ? t : { ...t, pinned }));
  const groups = pinned ? regroup(state.groups, [...ids], undefined) : state.groups;
  return tidy({ ...state, tabs: [...others.slice(0, at), ...moved, ...others.slice(at)], groups }, true);
}

/**
 * Drag and drop: moves the entry of `tabId` (its whole split view) to `toIndex` — counted in `tabs` without it,
 * clamped to its pin section — and into group `groupId` (none when undefined; ignored for pinned tabs).
 */
export function moveUnit(state: ShellState, tabId: string, toIndex: number, groupId: string | undefined): ShellState {
  const ids = splitMembers(state, tabId);
  const set = new Set(ids);
  const block = state.tabs.filter((t) => set.has(t.id));
  const head = block[0];
  if (head === undefined) return state;
  const others = state.tabs.filter((t) => !set.has(t.id));
  const pins = pinnedCount(others);
  const at = Math.max(head.pinned ? 0 : pins, Math.min(head.pinned ? pins : others.length, Math.floor(toIndex)));
  const tabs = [...others.slice(0, at), ...block, ...others.slice(at)];
  const target = head.pinned ? undefined : state.groups.find((g) => g.id === groupId);
  const shown = ids.some((id) => paneOfTab(state, id) !== undefined);
  const groups = regroup(state.groups, ids, target?.id).map((g) => (g.id === target?.id && shown && g.collapsed ? { ...g, collapsed: false } : g));
  return tidy({ ...state, tabs, groups }, true);
}

/** Drag and drop: moves a whole group to `toIndex` (counted in `tabs` without it; never into the pinned section or another group). */
export function moveGroup(state: ShellState, groupId: string, toIndex: number): ShellState {
  const g = state.groups.find((x) => x.id === groupId);
  if (g === undefined) return state;
  const set = new Set(g.tabIds);
  const block = state.tabs.filter((t) => set.has(t.id));
  const others = state.tabs.filter((t) => !set.has(t.id));
  let at = Math.max(pinnedCount(others), Math.min(others.length, Math.floor(toIndex)));
  // Landing inside another group: go after it.
  const prev = others[at - 1];
  const inside = prev === undefined ? undefined : groupOfTab(state.groups, prev.id);
  if (inside !== undefined && others[at] !== undefined && inside.tabIds.includes(others[at]!.id)) {
    while (at < others.length && inside.tabIds.includes(others[at]!.id)) at += 1;
  }
  return tidy({ ...state, tabs: [...others.slice(0, at), ...block, ...others.slice(at)] }, true);
}

/**
 * "Add to new group": the tabs (with their split partners) leave their groups and form group `newId` where the first
 * of them is, in the next free colour. Pinned tabs are unpinned.
 */
export function createGroup(state: ShellState, tabIds: string[], newId: string, name: string): ShellState {
  const ids = withPartners(state, tabIds);
  if (ids.length === 0 || state.groups.some((g) => g.id === newId)) return state;
  const set = new Set(ids);
  const tabs = state.tabs.map((t) => (set.has(t.id) && t.pinned ? { ...t, pinned: false } : t));
  const group: TabGroup = { id: newId, name: cleanName(name), color: nextGroupColor(state.groups), collapsed: false, tabIds: ids };
  return tidy({ ...state, tabs, groups: [...regroup(state.groups, ids, undefined), group] }, true);
}

/** "Add to group": the tabs (with their split partners) move to the end of `groupId`; pinned tabs are unpinned. */
export function addToGroup(state: ShellState, tabIds: string[], groupId: string): ShellState {
  const g = state.groups.find((x) => x.id === groupId);
  if (g === undefined) return state;
  const ids = withPartners(state, tabIds).filter((id) => !g.tabIds.includes(id));
  if (ids.length === 0) return state;
  const set = new Set(ids);
  const block = state.tabs.filter((t) => set.has(t.id)).map((t) => (t.pinned ? { ...t, pinned: false } : t));
  const others = state.tabs.filter((t) => !set.has(t.id));
  let last = -1;
  others.forEach((t, i) => {
    if (g.tabIds.includes(t.id)) last = i;
  });
  const shown = ids.some((id) => paneOfTab(state, id) !== undefined);
  const groups = regroup(state.groups, ids, groupId).map((x) => (x.id === groupId && shown && x.collapsed ? { ...x, collapsed: false } : x));
  return tidy({ ...state, tabs: [...others.slice(0, last + 1), ...block, ...others.slice(last + 1)], groups }, true);
}

/** "Remove from group": the tabs (with their split partners) leave their groups and move right after them. */
export function removeFromGroup(state: ShellState, tabIds: string[]): ShellState {
  const ids = withPartners(state, tabIds).filter((id) => groupOfTab(state.groups, id) !== undefined);
  if (ids.length === 0) return state;
  let tabs = state.tabs;
  for (const g of state.groups) {
    const out = new Set(g.tabIds.filter((id) => ids.includes(id)));
    const stay = g.tabIds.filter((id) => !out.has(id));
    if (out.size === 0 || stay.length === 0) continue;
    const block = tabs.filter((t) => out.has(t.id));
    const others = tabs.filter((t) => !out.has(t.id));
    let last = -1;
    others.forEach((t, i) => {
      if (stay.includes(t.id)) last = i;
    });
    tabs = [...others.slice(0, last + 1), ...block, ...others.slice(last + 1)];
  }
  return tidy({ ...state, tabs, groups: regroup(state.groups, ids, undefined) }, true);
}

/** "Ungroup": the group goes; its tabs stay where they are. */
export function ungroup(state: ShellState, groupId: string): ShellState {
  if (!state.groups.some((g) => g.id === groupId)) return state;
  return { ...state, groups: state.groups.filter((g) => g.id !== groupId) };
}

export function renameGroup(state: ShellState, groupId: string, name: string): ShellState {
  const clean = cleanName(name);
  if (!state.groups.some((g) => g.id === groupId && g.name !== clean)) return state;
  return { ...state, groups: state.groups.map((g) => (g.id === groupId ? { ...g, name: clean } : g)) };
}

export function setGroupColor(state: ShellState, groupId: string, color: GroupColor): ShellState {
  if (!state.groups.some((g) => g.id === groupId && g.color !== color)) return state;
  return { ...state, groups: state.groups.map((g) => (g.id === groupId ? { ...g, color } : g)) };
}

/**
 * Collapses or expands a group. Collapsing the group on screen selects the nearest tab outside it (right first), or a
 * new start page `newId` right after the group when there is none.
 */
export function setGroupCollapsed(state: ShellState, groupId: string, collapsed: boolean, newId: string): ShellState {
  const g = state.groups.find((x) => x.id === groupId);
  if (g === undefined || g.collapsed === collapsed) return state;
  const next: ShellState = { ...state, groups: state.groups.map((x) => (x.id === groupId ? { ...x, collapsed } : x)) };
  const inGroup = new Set(g.tabIds);
  if (!collapsed || !panes(state.layout).some((p) => inGroup.has(p.tabId))) return next;
  let last = -1;
  state.tabs.forEach((t, i) => {
    if (inGroup.has(t.id)) last = i;
  });
  const pick = nearestOpen(state.tabs, last, inGroup, state.groups);
  if (pick !== undefined) {
    // Selecting a tab in another collapsed group expands that one, not this one.
    return selectTab(next, pick, newId);
  }
  const home: Tab = { id: newId, url: state.homeUrl, back: [], forward: [], pinned: false };
  const tabs = [...state.tabs.slice(0, last + 1), home, ...state.tabs.slice(last + 1)];
  return selectTab({ ...next, tabs }, newId, newId);
}
